import React, { useState, useEffect } from 'react';
import axios from 'axios';
import packageJson from '../../../../package.json';
import loggerService, { LoggerLevel } from '../log';
import { clearCache, semverCompare } from './cacheBusterUtils';
import { LoadingScreen } from '@planitgeo/treeplotter-ui-library';

window.appVersion = packageJson.version;

interface CacheBusterProps {
  children: (props: {
    shouldRenderApp: boolean;
    clearCache: () => Promise<void>;
  }) => React.ReactNode;
}

const CacheBuster = ({ children }: CacheBusterProps) => {
  const [loading, setLoading] = useState(true);
  const [shouldRenderApp, setShouldRenderApp] = useState(false);

  useEffect(() => {
    const fetchManifest = async () => {
      // If offline, skip version check and allow app to load
      if (!navigator.onLine) {
        setShouldRenderApp(true);
        setLoading(false);
        return;
      }

      // Retry mechanism for page refresh
      const MAX_RETRIES = 3;
      const INITIAL_DELAY_MS = 1000; // 1 second
      const retryCountKey = 'cacheBusterRetryCount';
      const retryCount = parseInt(sessionStorage.getItem(retryCountKey) || '0', 10);

      if (retryCount >= MAX_RETRIES) {
        loggerService.log(
          LoggerLevel.WARN,
          `Cache busting failed after ${MAX_RETRIES} attempts. Allowing app to load with potentially stale version.`
        );
        sessionStorage.removeItem(retryCountKey);
        setShouldRenderApp(true); // Allow app to render anyway
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get('/manifest.json');
        const meta = response.data;

        const latestVersion = meta.version;

        if (!latestVersion) {
          // if no version is found, allow app to continue loading
          loggerService.log(
            LoggerLevel.DEBUG,
            'No version was found! Continuing to load the application.'
          );
          setShouldRenderApp(true);
        } else {
          const currentVersion = window.appVersion;

          const shouldForceRefresh = semverCompare(latestVersion, currentVersion) !== 0;

          if (shouldForceRefresh) {
            const attemptNumber = retryCount + 1;
            loggerService.log(
              LoggerLevel.INFO,
              `New version detected - ${latestVersion} (current: ${currentVersion}). Attempt ${attemptNumber}/${MAX_RETRIES}.`
            );
            setShouldRenderApp(false);

            // Increment retry count for exponential backoff
            try {
              sessionStorage.setItem(retryCountKey, String(attemptNumber));
            } catch (error) {
              loggerService.log(LoggerLevel.ERROR, 'Failed to persist retry count', error);
              setShouldRenderApp(true);
              setLoading(false);
              return;
            }

            // Calculate exponential backoff delay: 1s, 2s, 4s
            const delayMs = INITIAL_DELAY_MS * Math.pow(2, retryCount);

            if (retryCount > 0) {
              loggerService.log(
                LoggerLevel.INFO,
                `Waiting ${delayMs}ms before retry (exponential backoff)...`
              );
              await new Promise((resolve) => setTimeout(resolve, delayMs));
            }

            try {
              await clearCache();
              loggerService.log(LoggerLevel.INFO, 'Cache cleared successfully. Reloading...');
              window.location.reload();
            } catch (error) {
              loggerService.log(LoggerLevel.ERROR, 'Failed to clear cache before reload', error);
              window.location.reload();
            }
            return;
          } else {
            // Version matches, clear any retry count
            sessionStorage.removeItem(retryCountKey);
            loggerService.log(
              LoggerLevel.DEBUG,
              `You already have the latest version - ${latestVersion}.`
            );
            setShouldRenderApp(true);
          }
        }
        setLoading(false);
      } catch (error) {
        // If fetch fails (e.g., offline mode or network error), allow app to continue
        if (axios.isAxiosError(error) && !error.response) {
          loggerService.log(
            LoggerLevel.DEBUG,
            'Could not fetch manifest (network error). App will continue loading.'
          );
        } else {
          loggerService.log(LoggerLevel.ERROR, 'Failed to fetch manifest!!', error);
        }
        // Allow app to load even if version check fails
        setShouldRenderApp(true);
        setLoading(false);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchManifest();
  }, []);

  // Show loading screen while checking version or during cache busting
  if (loading) {
    return <LoadingScreen />;
  }

  return <> {children({ shouldRenderApp, clearCache })} </>;
};

export default CacheBuster;
